import _ from "lodash";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
  CATEGORY_SCORE,
  CATEGORY_GRADE,
  FINAL_GRADE,
  MYP_FINAL_GRADE,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_PYP_BENCHMARK,
  MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA,
} from "Constants/stringConstants";
import { getCriteriaSetLabels } from "Utils/GetCriteriaSetLabels";
import { getSettingValue } from "modules/PermissionModule";
import {
  GPA_ASSOCIATED_CRITERIA_TYPES_ELIGIBLE_FOR_AGGREGATION,
  MYP_CRITERIA_FINAL_SCORE,
  pypElementMapping,
} from "./constants";
import {
  canOverrideScoreInReports,
  getIsEnableFinalGradeForIL,
  getIsMYPCriteriaTotalAsAggregateEnabled,
  isNewDecimalSettingEnabled,
  getIsGradingHistoryEnhancementsEnabledInReports,
} from "./featureFlags";
import {
  getRating,
  getGpaAssociatedDataRating,
  getOverallRating,
  getFormattedGpaAssociatedRatingValue,
  getShowAsteriskOnRating,
  getUpdatedCriteriaValuesForMypFinalGrade,
  notExistsInScoreAggregationConfiguration,
  getCustomAcademicCriteriaValues,
  getStudentElementRatingValueOfCriteriaSet,
  getStudentElementRatingOfCriteriaSet,
  transformGradeScaleValuesToCriteriaValues,
} from "./ratingPrimitives";
import { getFinalScoreCriteriaSet } from "./scoreUtils/FinalScoreUtils";
import {
  getPYPAndDPLocalGradeCriteriaSet,
  getUBDLocalGradeCriteriaSet,
  getMYPLocalGradeCriteriaSet,
} from "./scoreUtils/LocalGradeUtils";
import { getFinalGradeCriteriaSet } from "./scoreUtils/FinalGradeUtils";
import { getAggregateRatingCriteriaSet } from "./scoreUtils/AggregateUtils";
import { getGradePointCriteriaSet } from "./scoreUtils/GradePointUtils";
import { getCreditCriteriaSet } from "./scoreUtils/CreditUtils";
import { getQualityPointCriteriaSet } from "./scoreUtils/QualityPointUtils";
import { getGpaCriteriaSet } from "./scoreUtils/GpaUtils";
import { getProjectsFinalGradeCriteriaSet } from "./scoreUtils/ProjectsFinalGradeUtils";
import { getProjectsFinalScoreCriteriaSet } from "./scoreUtils/ProjectsFinalScoreUtils";
import { getProjectsOverallProgressCriteriaSet } from "./scoreUtils/ProjectsOverallProgressUtils";
import { getMypCriteriaFinalScoreCriteriaSet } from "./scoreUtils/MypCriteriaFinalScoreUtils";

export {
  getRating,
  getGpaAssociatedDataRating,
  getOverallRating,
  getFormattedGpaAssociatedRatingValue,
  getShowAsteriskOnRating,
  getUpdatedCriteriaValuesForMypFinalGrade,
  notExistsInScoreAggregationConfiguration,
  getCustomAcademicCriteriaValues,
};

const getRatingAbbreviation = ({ valueId, academicCriteriaValues }) => {
  // handling valueId when '-' is clicked
  if (valueId == "") {
    return "-";
  }
  const { abbreviation } =
    _.find(academicCriteriaValues, value => {
      return _.includes(_.get(value, "id"), valueId);
    }) || {};

  return abbreviation;
};

const getDefaultGradeOptions = ({ localGradeSets }) => {
  const defaultOverallGradeSet = _.find(localGradeSets, { subType: "DEFAULT" });
  return _.get(defaultOverallGradeSet, "academicCriteriaValues", []);
};

const getTermGradeScaleOptions = ({ localGradeSets, subjectId }) =>
  _.get(
    _.find(localGradeSets, set => {
      const parentEntities = _.get(set, "parentEntities", []);
      return (
        _.some(parentEntities, { entityType: "ACADEMIC_TERM" }) &&
        _.some(parentEntities, { entityId: subjectId, entityType: "SUBJECT" })
      );
    }),
    "academicCriteriaValues",
    []
  );

const getCategoryGradeOptions = ({
  localGradeSets,
  categoryId,
  subjectId,
  gradeScaleType,
}) => {
  if (gradeScaleType == "CUSTOM") {
    return _.get(
      _.find(localGradeSets, set =>
        _.some(_.get(set, "parentEntities", []), {
          entityId: categoryId,
          entityType: "ACADEMIC_CRITERIA_VALUE",
        })
      ),
      "academicCriteriaValues",
      []
    );
  }

  let options =
    gradeScaleType == "TERM_GRADE_SCALE"
      ? getTermGradeScaleOptions({ localGradeSets, subjectId })
      : [];

  if (_.isEmpty(options)) {
    options = getDefaultGradeOptions({ localGradeSets });
  }
  return options;
};

const getSubjectLevelGradeOptions = ({
  localGradeSets,
  subjectId,
  curriculumType,
  academicTermId,
  gradingPeriodType,
}) => {
  let options = _.get(
    _.find(localGradeSets, set => {
      const parentEntities = _.get(set, "parentEntities", []);
      return (
        (gradingPeriodType == "CUMULATIVE"
          ? _.some(parentEntities, {
              entityType: "ACADEMIC_TERM",
              entityId: academicTermId,
            })
          : true) &&
        _.some(parentEntities, { entityType: "ACADEMIC_TERM" }) &&
        _.some(parentEntities, { entityId: subjectId, entityType: "SUBJECT" })
      );
    }),
    "academicCriteriaValues",
    []
  );

  if (_.isEmpty(options)) {
    options = getDefaultGradeOptions({ localGradeSets });
  }

  if (!_.isEmpty(localGradeSets) && curriculumType == CURRICULUM_TYPE_PYP) {
    options = _.first(localGradeSets)?.academicCriteriaValues || [];
  }

  return options;
};

const getOverallGradeOptions = ({
  localGradeSets,
  categoryId = null,
  subjectId,
  matchedRating,
  gradeScaleType,
  curriculumType,
  academicTermId,
  gradingPeriodType,
}) => {
  let options = categoryId
    ? getCategoryGradeOptions({
        localGradeSets,
        categoryId,
        subjectId,
        gradeScaleType,
      })
    : getSubjectLevelGradeOptions({
        localGradeSets,
        subjectId,
        curriculumType,
        academicTermId,
        gradingPeriodType,
      });

  //This is to give an option to remove the grade
  if (!_.isEmpty(options)) {
    options = _.concat([{ label: "-", id: "overall_grade" }], options);
  }

  //This is to handle the case when there is a rating added
  //but the grade is not present in the grade set
  //This happens when the grade set is changes ( custom -> default, or default -> custom ) etc
  if (
    _.get(matchedRating, "value", null) &&
    !_.some(options, { label: matchedRating.value })
  ) {
    options = {
      ...options,
      ...[
        {
          label: _.get(matchedRating, "value", "-"),
          id: "matched_rating",
          disabled: true,
        },
      ],
    };
  }

  return _.map(options, option => {
    return {
      label: option.label,
      value: option.id,
      isDisabled: _.get(option, "disabled", false),
    };
  });
};

const CURRICULUM_CRITERIA__MAP = {
  [CURRICULUM_TYPE_PYP]: {
    FINAL_SCORE: getFinalScoreCriteriaSet,
    LOCAL_GRADE: getPYPAndDPLocalGradeCriteriaSet,
    FINAL_GRADE: getFinalGradeCriteriaSet,
    AGGREGATE: getAggregateRatingCriteriaSet,
    GRADE_POINT: getGradePointCriteriaSet,
    CREDIT: getCreditCriteriaSet,
    QUALITY_POINT: getQualityPointCriteriaSet,
    GRADE_POINT_AVERAGE: getGpaCriteriaSet,
  },
  [CURRICULUM_TYPE_UBD]: {
    FINAL_SCORE: getFinalScoreCriteriaSet,
    LOCAL_GRADE: getUBDLocalGradeCriteriaSet,
    FINAL_GRADE: getFinalGradeCriteriaSet,
    AGGREGATE: getAggregateRatingCriteriaSet,
    GRADE_POINT: getGradePointCriteriaSet,
    CREDIT: getCreditCriteriaSet,
    QUALITY_POINT: getQualityPointCriteriaSet,
    GRADE_POINT_AVERAGE: getGpaCriteriaSet,
  },
  [CURRICULUM_TYPE_MYP]: {
    FINAL_SCORE: getFinalScoreCriteriaSet,
    LOCAL_GRADE: getMYPLocalGradeCriteriaSet,
    FINAL_GRADE: getFinalGradeCriteriaSet,
    AGGREGATE: getAggregateRatingCriteriaSet,
    MYP_CRITERIA_FINAL_SCORE: getMypCriteriaFinalScoreCriteriaSet,
    PROJECT_FINAL_GRADE: getProjectsFinalGradeCriteriaSet,
    PROJECT_OVERALL_PROGRESS: getProjectsOverallProgressCriteriaSet,
    GRADE_POINT: getGradePointCriteriaSet,
    CREDIT: getCreditCriteriaSet,
    QUALITY_POINT: getQualityPointCriteriaSet,
    GRADE_POINT_AVERAGE: getGpaCriteriaSet,
  },
  [CURRICULUM_TYPE_DP]: {
    FINAL_SCORE: getFinalScoreCriteriaSet,
    LOCAL_GRADE: getPYPAndDPLocalGradeCriteriaSet,
    FINAL_GRADE: getFinalGradeCriteriaSet,
    AGGREGATE: getAggregateRatingCriteriaSet,
    PROJECT_FINAL_SCORE: getProjectsFinalScoreCriteriaSet,
    PROJECT_FINAL_GRADE: getProjectsFinalGradeCriteriaSet,
    CRITERIA_RUBRIC_FINAL_GRADE: getProjectsFinalGradeCriteriaSet,
    PROJECT_OVERALL_PROGRESS: getProjectsOverallProgressCriteriaSet,
    GRADE_POINT: getGradePointCriteriaSet,
    CREDIT: getCreditCriteriaSet,
    QUALITY_POINT: getQualityPointCriteriaSet,
    GRADE_POINT_AVERAGE: getGpaCriteriaSet,
  },
};

const getGradeBoundaryRatingEnabled = ({ academicCriteriaSets }) =>
  _.find(academicCriteriaSets, { criteriaType: "GRADE_BOUNDARY" })
    ?.isRatingEnabled;

const hasGradeBoundaryForCategory = ({
  gradeBoundaryCriteriaSets,
  categoryValueId,
}) =>
  _.some(gradeBoundaryCriteriaSets, gradeBoundarySet =>
    _.some(
      _.get(gradeBoundarySet, "parentEntities", []),
      parentEntity => parentEntity.entityId === categoryValueId
    )
  );

const getIbGradeEnabled = ({
  mode,
  academicCriteriaSets,
  isGradeBoundariesEnabled,
  showGrade,
}) => {
  if (mode == "preview") {
    return getGradeBoundaryRatingEnabled({ academicCriteriaSets }) && showGrade;
  }
  return (
    getGradeBoundaryRatingEnabled({ academicCriteriaSets }) &&
    isGradeBoundariesEnabled &&
    showGrade
  );
};

const getCategoryFinalScoreEnabled = ({
  academicCriteriaSets,
  overallScoreForGradingPeriod,
}) =>
  _.find(academicCriteriaSets, { criteriaType: "ASSESSMENT_CATEGORY" })
    ?.isRatingEnabled && overallScoreForGradingPeriod;

const buildScoreEntry = ({
  score,
  scoreValue,
  categoryScoreLabel,
  showWarning,
  calculatedGrade,
  updatedCalculatedGrade,
  categoryWeightage,
  totalScore,
  scoreValues,
}) => ({
  id: "SCORE",
  value: !_.isNil(score) ? score : "-",
  label: categoryScoreLabel,
  showWarning,
  rawCategoryScore: scoreValue,
  displayCategoryScore: score,
  rawCalculatedCategoryScore: calculatedGrade,
  displayCalculatedCategoryScore: updatedCalculatedGrade,
  rawCategoryWeightage: categoryWeightage,
  displayCategoryWeightage: totalScore,
  actualWeightage: _.get(_.first(scoreValues), "value", ""),
});

const findGradeBoundaryCategoryGrade = ({
  gradeBoundaryCriteriaSets,
  ratings,
  categoryValueId,
}) => {
  let categoryGradeEquivalent = "-";
  _.forEach(gradeBoundaryCriteriaSets, gradeBoundarySet => {
    const gradeBoundaryid = _.get(gradeBoundarySet, "id", "");
    const parentEntities = _.get(gradeBoundarySet, "parentEntities", []);
    const parentEntity = _.find(
      parentEntities,
      pe => pe.entityId === categoryValueId
    );

    if (!_.isUndefined(parentEntity)) {
      const matchedRating = _.find(
        ratings,
        rating => rating.academicCriteriaSet === gradeBoundaryid
      );
      categoryGradeEquivalent = _.get(matchedRating, "value", "");
    }
  });
  return categoryGradeEquivalent;
};

export const getDynamicGradeSets = ({
  termId = "",
  childGradingPeriod,
  total = 0,
  ratings,
  gpaAssociatedData = {},
  gradeNode = {},
  mode,
  dynamicCriteriaTypes = ["FINAL_GRADE", "LOCAL_GRADE"],
  isRatingEnabled,
  parentEntity = {},
  academicCriteriaSets,
  shouldNotCalculateFinalGradeFromTotal,
  subjectId,
  curriculumType,
  categoryFinalScoreDisplayType,
  useWeightageForCategory = false,
  t,
  data,
  currentCurriculumProgramId,
  gradingPeriodType = "REPORTING",
  localGradeCalculationMethod,
  isNewAssessmentSettingEnabled = false,
  finalGradeCalculationSetting,
  overallScoreForGradingPeriod,
  overrideFinalGradeSetting,
  termFinalScoreDisplayType,
  weightageType,
  grade,
  uid,
  subjects,
  isScoreAggregationEnabled,
  isScoreAndGradeAggregationEnabled,
  scoreAggregationConfiguration,
  criteriaParentType,
  aggregateParentCriteriaSet,
  termCriteriaAsteriskMap,
  organizationId,
  isFreezed,
}) => {
  const reportGradeId = _.get(grade, "id", "");
  const result = [];
  const filteredCriteriaSets = _.filter(academicCriteriaSets, set => {
    return _.includes(dynamicCriteriaTypes, set.criteriaType);
  });

  const categoryCalculationHasPercentage = isNewAssessmentSettingEnabled
    ? categoryFinalScoreDisplayType == "PERCENTAGE"
    : weightageType == "PERCENTAGE";

  const subjectType = _.get(data, "type", "");

  _.map(filteredCriteriaSets, criteriaSet => {
    const criteriaType = _.get(criteriaSet, "criteriaType");
    const academicCriteriaValues = _.get(criteriaSet, "academicCriteriaValues");
    let isRatingEnabled = _.get(criteriaSet, "isRatingEnabled");
    const subType = _.get(criteriaSet, "subType");
    if (criteriaType == "FINAL_GRADE") {
      if (canOverrideScoreInReports()) {
        const getCriteriaSet =
          CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

        const finalGradeCriteriaSet = getCriteriaSet({
          criteriaSet,
          ratings,
          subjectId,
          gradingPeriodType,
          termId,
          curriculumType,
          shouldNotCalculateFinalGradeFromTotal,
          total,
          isScoreAggregationEnabled,
          isScoreAndGradeAggregationEnabled,
          scoreAggregationConfiguration,
          academicCriteriaSets,
          uid,
          termCriteriaAsteriskMap,
        });

        // For DP TOK there is different final grade criteria set
        // For TOK we will add only add TOK specific final grade criteria set
        // For subjects we we will add only add subject specific final grade criteria set
        if (
          (subjectType === "TOK" && subType === "TOK") ||
          (subjectType !== "TOK" && subType !== "TOK")
        ) {
          result.push(finalGradeCriteriaSet);
        }
      } else {
        let value;
        let id;
        let overRidingValue;
        let overRidingValueId;
        let overRidingRatingId;
        let isRemovedGrade;

        const updatedCriteriaValues = getUpdatedCriteriaValuesForMypFinalGrade({
          academicCriteriaSets,
          academicCriteriaValues,
          uid,
        });

        if (!shouldNotCalculateFinalGradeFromTotal) {
          // calculate based on total
          // if total falls in between of boundary guidelines
          // get that criteria value

          const matchedFinalGradeCriteriaValue = _.find(
            updatedCriteriaValues,
            value => {
              return _.includes(_.get(value, "boundary_guidelines"), total);
            }
          );

          const { id: valueId, abbreviation } =
            matchedFinalGradeCriteriaValue || {};
          // if value exists, get abbreviation
          value = !_.isEmpty(valueId) ? abbreviation : "-";
          id = !_.isEmpty(valueId) ? valueId : "final_grade";
        } else {
          // get from ratings
          const { id: gradeNodeId, itemId, pypType } = gradeNode;

          const valueIdParams = {
            setId: criteriaSet.id,
            type: pypElementMapping[pypType],
            nodeId: gradeNodeId,
            itemId,
            ratings,
          };

          // extracting the valueId and isRemoved prop for the calculated rating
          const {
            valueId,
            isRemoved,
          } = getStudentElementRatingValueOfCriteriaSet({
            ...valueIdParams,
            isComputedRating: true,
            returnIsRemoved: true,
            gradingPeriodType,
            termId,
            checkCategoryId: true,
            categoryId: null,
          });

          isRemovedGrade = isRemoved;

          const overRidingRating = getStudentElementRatingOfCriteriaSet({
            ...valueIdParams,
            isComputedRating: false,
            gradingPeriodType,
            termId,
            checkCategoryId: true,
            categoryId: null,
          });

          overRidingValueId = overRidingRating?.academicCriteriaValue;
          overRidingRatingId = overRidingRating?.id;

          const abbreviation = getRatingAbbreviation({
            valueId,
            academicCriteriaValues,
          });

          overRidingValue = getRatingAbbreviation({
            valueId: overRidingRating?.academicCriteriaValue,
            academicCriteriaValues,
          });
          value = !_.isEmpty(valueId) ? abbreviation : "-";
          id = !_.isEmpty(valueId) ? valueId : "final_grade";
          // If rating is removed then overRidingValue should be equal to '-'
          if (isRemoved) {
            overRidingValue = "-";
          }

          const isNewFlowEnabledForDP = curriculumType == CURRICULUM_TYPE_DP;

          const isNewRatingsArchitecture =
            (curriculumType == CURRICULUM_TYPE_MYP &&
              finalGradeCalculationSetting == "SCORE") ||
            isNewFlowEnabledForDP;

          if (isNewRatingsArchitecture) {
            const editedRating = getRating({
              computedRating: false,
              ratings,
              overallGradeSet: criteriaSet,
              subjectId,
              categoryId: null,
              gradingPeriodType,
              termId,
            });

            const computedRating = getRating({
              computedRating: true,
              ratings,
              overallGradeSet: criteriaSet,
              subjectId,
              categoryId: null,
              skipIsRemoved: true,
              gradingPeriodType,
              termId,
            });

            value = _.get(computedRating, "value", "-");
            id = _.get(
              _.find(academicCriteriaValues, { label: value }),
              "id",
              "-"
            );

            overRidingValue = editedRating?.value;
            overRidingValueId = _.get(
              _.find(academicCriteriaValues, {
                label: overRidingValue,
              }),
              "id",
              "-"
            );

            isRemovedGrade = false;

            if (
              _.has(computedRating, "isRemoved") &&
              computedRating?.isRemoved &&
              !editedRating
            ) {
              overRidingValue = "-";
              isRemovedGrade = true;
            }
          }
        }
        if (
          (subjectType === "TOK" && subType === "TOK") ||
          (subjectType !== "TOK" && subType !== "TOK")
        ) {
          const overridingDetails = overrideFinalGradeSetting
            ? { overRidingValue, overRidingValueId, overRidingRatingId }
            : {};

          // For now score aggregation is enabled in cumulative report section view only
          const showAsteriskOnRating = getShowAsteriskOnRating({
            isScoreAggregationEnabled,
            gradingPeriodType,
            scoreAggregationConfiguration,
            subjectId,
            academicCriteriaSetId: criteriaSet.id,
            academicTermId: termId,
          });

          result.push({
            ...criteriaSet,
            id: criteriaSet.id,
            label: criteriaSet.label,
            value,
            valueId: id,
            ...overridingDetails,
            isRemovedGrade,
            showAsteriskOnRating,
            customAcademicCriteriaValues: updatedCriteriaValues,
          });
        }
      }
    }

    if (criteriaType == "LOCAL_GRADE") {
      if (subjectType !== "TOK" && subType != "CATEGORY_GRADE_SCALE") {
        if (canOverrideScoreInReports()) {
          const getCriteriaSet =
            CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

          const localGradeCriteriaSet = getCriteriaSet({
            criteriaSet,
            ratings,
            subjectId,
            gradingPeriodType,
            termId,
            curriculumType,
            academicCriteriaSets,
            subjects,
            localGradeCalculationMethod,
            reportGradeId,
            uid,
          });

          result.push(localGradeCriteriaSet);
        } else {
          let matchedLocalGradeCriteriaValue = {};

          //In UBd, we find local grade based on subjectId and criteriaSet id
          //In main local grade, teh categoryId is null
          if (
            _.includes(
              [CURRICULUM_TYPE_UBD, CURRICULUM_TYPE_PYP],
              curriculumType
            )
          ) {
            const { rating, showWarning, calculatedGrade } = getOverallRating({
              ratings,
              subjectId,
              categoryId: null,
              overallGradeSet: criteriaSet,
              criteriaSet,
              curriculumType,
              subjectType,
              academicCriteriaSets,
              gradingPeriodType,
              termId,
            });

            const localGradeSets = _.filter(academicCriteriaSets, {
              criteriaType: "LOCAL_GRADE",
            });

            const updatedOptions = getOverallGradeOptions({
              localGradeSets,
              categoryId: null,
              subjectId,
              matchedRating: rating,
              curriculumType,
              academicTermId: termId,
              gradingPeriodType,
            });

            const customAcademicCriteriaValues = getCustomAcademicCriteriaValues(
              {
                academicCriteriaSets,
                subjectId,
                curriculumType,
                termId,
                gradingPeriodType,
              }
            );

            const getValueId = ({ label }) =>
              _.get(
                _.find(updatedOptions, option => {
                  return option.label == label;
                }),
                "value",
                "overall_grade"
              );

            result.push({
              ...criteriaSet,
              id: criteriaSet.id,
              label: criteriaSet.label,
              customAcademicCriteriaValues,
              value: [
                {
                  valueId: getValueId({ label: rating?.value }),
                  value: rating?.value ? rating?.value : "-",
                  options: updatedOptions,
                  showOverallGradeDropdown: true,
                  disabled: !overrideFinalGradeSetting,
                  academicCriteriaSetId: criteriaSet.id,
                  academicCriteriaValue: null,
                  showWarning:
                    showWarning && finalGradeCalculationSetting != "MANUAL",
                },
              ],
              valueId: "local_grade",
              calculatedGrade,
              calculatedGradeId: getValueId({ label: calculatedGrade }),
            });
          } else {
            //in DP, we find local grade based on academicCriteriaValueId, in other cases we find it based on grade_id and final_grade_value_id from academicCriteriaValues
            if (
              (_.isEqual(CURRICULUM_TYPE_DP, curriculumType) &&
                localGradeCalculationMethod == "WEIGHTED_SCORE") ||
              (_.isEqual(CURRICULUM_TYPE_MYP, curriculumType) &&
                _.includes(
                  ["SCORE", "MYP_CRITERIA"],
                  finalGradeCalculationSetting
                ) &&
                //In MYP still the value is "SCORE_BASED_ASSESSMENT" now, soon it will be changed
                //to "WEIGHTED_SCORE", so making it backward compatible
                _.includes(
                  ["SCORE_BASED_ASSESSMENT", "WEIGHTED_SCORE"],
                  localGradeCalculationMethod
                ))
            ) {
              const ratingsFilter = {
                academicCriteriaSet: criteriaSet.id,
                ibPYPElementId: subjectId,
              };

              if (gradingPeriodType == "CUMULATIVE") {
                ratingsFilter["academicTermId"] = termId;
              }

              const academicCriteriaValueId = _.get(
                _.find(ratings, ratingsFilter),
                "academicCriteriaValue",
                ""
              );

              matchedLocalGradeCriteriaValue = _.find(academicCriteriaValues, {
                id: academicCriteriaValueId,
              });
            } else {
              const {
                valueId = null,
                overRidingValueId = null,
                academicCriteriaValues: finalGradeCriteriaValues,
                isRemovedGrade,
              } =
                _.find(
                  result,
                  item => _.get(item, "criteriaType") === "FINAL_GRADE"
                ) || {};

              let finalGradeValueId =
                overRidingValueId && overRidingValueId != "-"
                  ? overRidingValueId
                  : valueId;

              if (isRemovedGrade) {
                finalGradeValueId = "-";
              }

              if (_.isEqual(CURRICULUM_TYPE_DP, curriculumType)) {
                const selectedValueDetails = _.find(finalGradeCriteriaValues, {
                  id: finalGradeValueId,
                });

                const mappedLocalGrade = _.get(
                  selectedValueDetails,
                  "localGrades[0]",
                  {}
                );

                matchedLocalGradeCriteriaValue = _.find(
                  academicCriteriaValues,
                  {
                    id: mappedLocalGrade.localGradeCriteriaValueId,
                  }
                );
              } else if (
                _.isEqual(CURRICULUM_TYPE_MYP, curriculumType) &&
                finalGradeCalculationSetting == "SCORE"
              ) {
                const selectedValueDetails = _.find(finalGradeCriteriaValues, {
                  id: finalGradeValueId,
                });

                const mappedLocalGrade = _.find(
                  selectedValueDetails?.localGrades,
                  item => item?.gradeId == grade?.id
                );

                matchedLocalGradeCriteriaValue = _.find(
                  academicCriteriaValues,
                  {
                    local_grade_value_id:
                      mappedLocalGrade?.localGradeCriteriaValueId,
                  }
                );
              } else {
                matchedLocalGradeCriteriaValue = _.find(
                  academicCriteriaValues,
                  value => {
                    return (
                      value.grade_id == _.get(parentEntity, "id") &&
                      value.final_grade_value_id == finalGradeValueId
                    );
                  }
                );
              }
            }

            const { label: abbreviation } =
              matchedLocalGradeCriteriaValue || {};

            result.push({
              ...criteriaSet,
              id: criteriaSet.id,
              label: criteriaSet.label,
              value: !_.isEmpty(matchedLocalGradeCriteriaValue)
                ? abbreviation
                : "-",
              valueId: "local_grade",
            });
          }
        }
      }
    }

    if (criteriaType == "FINAL_SCORE") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const finalScoreCriteriaSet = getCriteriaSet({
        ratings,
        criteriaSet,
        subjectId,
        termId,
        gradingPeriodType,
        termFinalScoreDisplayType,
        childGradingPeriod,
        curriculumType,
        subjectType,
        academicCriteriaSets,
        isScoreAggregationEnabled,
        isScoreAndGradeAggregationEnabled,
        scoreAggregationConfiguration,
        termCriteriaAsteriskMap,
      });

      result.push(finalScoreCriteriaSet);
    }

    if (
      (criteriaType == "ASSESSMENT_CATEGORY" && mode == "preview") ||
      (criteriaType == "ASSESSMENT_CATEGORY" &&
        mode == "response" &&
        !_.isEmpty(
          _.find(criteriaSet.parentEntities, {
            entityType: "SUBJECT",
            entityId: subjectId,
          })
        ))
    ) {
      const criteriaLabels = getCriteriaSetLabels({
        organizationId,
        curriculumProgramId: currentCurriculumProgramId,
        criteriaTypes: [CATEGORY_SCORE, CATEGORY_GRADE],
        academicCriteriaSets,
      });
      const categoryScoreLabel = _.get(
        criteriaLabels,
        CATEGORY_SCORE,
        t("common:score")
      );
      const categoryGradeLabel = _.get(
        criteriaLabels,
        CATEGORY_GRADE,
        curriculumType == CURRICULUM_TYPE_UBD
          ? t("common:grade")
          : t("common:IB_grade")
      );

      _.forEach(
        criteriaSet.academicCriteriaValues,
        ({ id, label, scoreValues, gradeScaleType }) => {
          //the weightage for this category in terms of final score (example 40 out of 100)
          let weightage = _.get(_.first(scoreValues), "value", "");
          // the score obtained for this category will be in the "value" property of item in ratings array when filtered through academicCriteriaSetId and academicCriteriaValueId

          let scoreValue;
          let calculatedGrade;
          let showWarning;
          // When setting is on percentage weightage will be 100
          const categoryWeightage = categoryCalculationHasPercentage
            ? 100
            : weightage;

          if (canOverrideScoreInReports()) {
            const finalScoreCriteriaSet = _.find(academicCriteriaSets, {
              criteriaType: "FINAL_SCORE",
            });

            // Use edited ASSESSMENT_CATEGORY score if it is available
            // Else use computed ASSESSMENT_CATEGORY score
            // The  ASSESSMENT_CATEGORY score can have either "FINAL_SCORE" or "ASSESSMENT_CATEGORY" criteria set
            // We use customOverallGradeSets for this
            const overallRating = getOverallRating({
              overallGradeSet: criteriaSet,
              customOverallGradeSets: [
                finalScoreCriteriaSet?.id,
                criteriaSet?.id,
              ],
              ratings,
              subjectId,
              categoryId: id,
              curriculumType,
              subjectType,
              academicCriteriaSets,
              criteriaSet: {},
            });

            scoreValue = _.get(overallRating, "rating.value", null);
            calculatedGrade = _.get(overallRating, "calculatedGrade", null);
            showWarning = _.get(overallRating, "showWarning", false);
          } else {
            scoreValue = _.get(
              _.find(ratings, {
                academicCriteriaSet: criteriaSet.id,
                academicCriteriaValue: id,
              }),
              "value",
              null
            );
          }

          let score = scoreValue;
          let updatedCalculatedGrade = calculatedGrade;

          // Previous locked reports will work as before
          if (!isNewDecimalSettingEnabled()) {
            score = !_.isNil(scoreValue) ? _.round(scoreValue, 2) : scoreValue;
            updatedCalculatedGrade =
              !_.isNil(calculatedGrade) && calculatedGrade != "-"
                ? _.round(calculatedGrade, 2)
                : calculatedGrade;
          }

          if (mode == "response" && categoryCalculationHasPercentage) {
            score =
              _.isNil(scoreValue) ||
              scoreValue == "-" ||
              (_.isString(scoreValue) && _.isEmpty(scoreValue))
                ? "-"
                : `${_.toString(score)}%`;
            updatedCalculatedGrade =
              !_.isNil(calculatedGrade) && calculatedGrade != "-"
                ? `${_.toString(calculatedGrade)}%`
                : "-";

            weightage =
              !_.isNil(weightage) && weightage !== ""
                ? `${_.toString(_.round(weightage, 2))}%`
                : "-";
          }
          if (mode == "preview") {
            weightage = "%";
          }
          const totalScore = categoryCalculationHasPercentage
            ? "100%"
            : weightage;

          let value;

          const isShowGradeBoundaries = _.get(
            criteriaSet,
            "isShowGradeBoundaries",
            true
          );

          const showGrade = !_.isEqual(finalGradeCalculationSetting, "NONE");

          const isGradingHistoryEnhancementsEnabledInReports = getIsGradingHistoryEnhancementsEnabledInReports();

          if (curriculumType == CURRICULUM_TYPE_DP) {
            value = [];

            const finalScoreEnabled = getCategoryFinalScoreEnabled({
              academicCriteriaSets,
              overallScoreForGradingPeriod,
            });

            const gradeBoundaryCriteriaSets = _.filter(academicCriteriaSets, {
              criteriaType: "GRADE_BOUNDARY",
            });

            const isGradeBoundariesEnabled =
              finalGradeCalculationSetting == "MANUAL"
                ? isShowGradeBoundaries
                : isShowGradeBoundaries &&
                  hasGradeBoundaryForCategory({
                    gradeBoundaryCriteriaSets,
                    categoryValueId: id,
                  });

            const ibGradeEnabled = getIbGradeEnabled({
              mode,
              academicCriteriaSets,
              isGradeBoundariesEnabled,
              showGrade,
            });

            if (finalScoreEnabled) {
              value.push(
                buildScoreEntry({
                  score,
                  scoreValue,
                  categoryScoreLabel,
                  showWarning,
                  calculatedGrade,
                  updatedCalculatedGrade,
                  categoryWeightage,
                  totalScore,
                  scoreValues,
                })
              );
            }

            if (ibGradeEnabled) {
              //We are supporting editing in new architecture

              let categoryGradeEquivalent = findGradeBoundaryCategoryGrade({
                gradeBoundaryCriteriaSets,
                ratings,
                categoryValueId: id,
              });

              const defaultGradeSet = _.find(filteredCriteriaSets, {
                criteriaType: "FINAL_GRADE",
                subType: subjectType == "TOK" ? "TOK" : "SUBJECT",
              });

              //academicCriteriaSet ->  is the id of the default local grade criteria set
              //categoryId -> is the id of the category
              //ibPYPElementId ->: is the id of the subject

              const {
                rating: matchedRating,
                showWarning,
                calculatedGrade,
              } = getOverallRating({
                overallGradeSet: defaultGradeSet,
                ratings,
                subjectId,
                categoryId: id,
                criteriaSet: { id, label, criteriaType },
                curriculumType,
                subjectType,
                academicCriteriaSets,
              });

              categoryGradeEquivalent = _.get(matchedRating, "value", "-");

              let updatedOptions = _.map(
                _.get(defaultGradeSet, "academicCriteriaValues", []),
                option => {
                  return {
                    label: option.label,
                    value: option.id,
                  };
                }
              );

              if (
                categoryGradeEquivalent != "-" ||
                isGradingHistoryEnhancementsEnabledInReports
              ) {
                updatedOptions = _.concat(
                  [{ label: "-", value: "overall_grade" }],
                  updatedOptions
                );
              }

              value.push({
                valueId: _.get(
                  _.find(updatedOptions, option => {
                    return option.label == categoryGradeEquivalent;
                  }),
                  "value",
                  "overall_grade"
                ),
                id: "IB_GRADE",
                value: categoryGradeEquivalent,
                label: categoryGradeLabel,
                options: updatedOptions,
                showOverallGradeDropdown: true,
                disabled:
                  finalGradeCalculationSetting != "MANUAL" &&
                  !overrideFinalGradeSetting,
                academicCriteriaSetId: defaultGradeSet?.id,
                academicCriteriaValue: id,
                showWarning:
                  showWarning && finalGradeCalculationSetting != "MANUAL",
                calculatedGrade,
              });
            }

            isRatingEnabled = finalScoreEnabled || ibGradeEnabled;
          } else if (curriculumType == CURRICULUM_TYPE_UBD) {
            value = [];

            const finalScoreEnabled = getCategoryFinalScoreEnabled({
              academicCriteriaSets,
              overallScoreForGradingPeriod,
            });

            const isCategoryOverallGradeEnabled =
              mode == "preview"
                ? _.find(academicCriteriaSets, item => {
                    return (
                      item.criteriaType == "LOCAL_GRADE" &&
                      item.subType == "CATEGORY_GRADE_SCALE"
                    );
                  })?.isRatingEnabled && showGrade
                : _.find(academicCriteriaSets, item => {
                    return (
                      item.criteriaType == "LOCAL_GRADE" &&
                      item.subType == "CATEGORY_GRADE_SCALE"
                    );
                  })?.isRatingEnabled &&
                  !_.isNull(gradeScaleType) &&
                  isShowGradeBoundaries &&
                  showGrade;

            if (finalScoreEnabled) {
              value.push({
                id: "SCORE",
                value: score ? score : "-",
                label: categoryScoreLabel,
                showWarning,
                rawCategoryScore: scoreValue,
                displayCategoryScore: score,
                rawCalculatedCategoryScore: calculatedGrade,
                displayCalculatedCategoryScore: updatedCalculatedGrade,
                rawCategoryWeightage: categoryWeightage,
                displayCategoryWeightage: totalScore,
                actualWeightage: _.get(_.first(scoreValues), "value", ""),
              });
            }

            if (isCategoryOverallGradeEnabled) {
              const defaultOverallGradeSet = _.find(filteredCriteriaSets, {
                criteriaType: "LOCAL_GRADE",
                subType: "DEFAULT",
              });

              //academicCriteriaSet ->  is the id of the default local grade criteria set
              //categoryId -> is the id of the category
              //ibPYPElementId ->: is the id of the subject

              const {
                rating: matchedRating,
                showWarning,
                calculatedGrade,
              } = getOverallRating({
                overallGradeSet: _.find(academicCriteriaSets, {
                  criteriaType: "LOCAL_GRADE",
                  subType: "DEFAULT",
                }),
                ratings,
                subjectId,
                categoryId: id,
                criteriaSet: { id, label, criteriaType },
                curriculumType,
                subjectType,
                academicCriteriaSets,
              });

              const categoryGradeEquivalent = _.get(
                matchedRating,
                "value",
                "-"
              );

              const localGradeSets = _.filter(academicCriteriaSets, {
                criteriaType: "LOCAL_GRADE",
              });

              const updatedOptions = getOverallGradeOptions({
                localGradeSets,
                categoryId: id,
                subjectId,
                matchedRating,
                gradeScaleType,
              });

              value.push({
                valueId: _.get(
                  _.find(updatedOptions, option => {
                    return option.label == categoryGradeEquivalent;
                  }),
                  "value",
                  "overall_grade"
                ),
                id: "GRADE",
                value: categoryGradeEquivalent,
                label: categoryGradeLabel,
                options: updatedOptions,
                showOverallGradeDropdown: true,
                disabled:
                  finalGradeCalculationSetting != "MANUAL" &&
                  !overrideFinalGradeSetting,
                academicCriteriaSetId: defaultOverallGradeSet?.id,
                academicCriteriaValue: id,
                showWarning:
                  showWarning && finalGradeCalculationSetting != "MANUAL",
                calculatedGrade,
              });
            }

            isRatingEnabled =
              finalScoreEnabled || isCategoryOverallGradeEnabled;
          } else if (curriculumType == CURRICULUM_TYPE_PYP) {
            value = [];

            const finalScoreEnabled = getCategoryFinalScoreEnabled({
              academicCriteriaSets,
              overallScoreForGradingPeriod,
            });

            if (finalScoreEnabled) {
              value.push({
                id: "SCORE",
                value: score ? score : "-",
                label: categoryScoreLabel,
                showWarning,
                rawCategoryScore: scoreValue,
                displayCategoryScore: score,
                rawCalculatedCategoryScore: calculatedGrade,
                displayCalculatedCategoryScore: updatedCalculatedGrade,
                rawCategoryWeightage: categoryWeightage,
                displayCategoryWeightage: totalScore,
                actualWeightage: _.get(_.first(scoreValues), "value", ""),
              });
            }

            isRatingEnabled = finalScoreEnabled;
          } else {
            value = [];
            const finalScoreEnabled = getCategoryFinalScoreEnabled({
              academicCriteriaSets,
              overallScoreForGradingPeriod,
            });

            const gradeBoundaryCriteriaSets = _.filter(academicCriteriaSets, {
              criteriaType: "GRADE_BOUNDARY",
            });

            const isGradeBoundariesEnabled =
              finalGradeCalculationSetting == "MYP_CRITERIA"
                ? isShowGradeBoundaries
                : isShowGradeBoundaries &&
                  hasGradeBoundaryForCategory({
                    gradeBoundaryCriteriaSets,
                    categoryValueId: id,
                  });

            const ibGradeEnabled = getIbGradeEnabled({
              mode,
              academicCriteriaSets,
              isGradeBoundariesEnabled,
              showGrade,
            });

            if (finalScoreEnabled) {
              value.push({
                id: "SCORE",
                value: score ? score : "-",
                label: categoryScoreLabel,
                showWarning,
                rawCategoryScore: scoreValue,
                displayCategoryScore: score,
                rawCalculatedCategoryScore: calculatedGrade,
                displayCalculatedCategoryScore: updatedCalculatedGrade,
                rawCategoryWeightage: categoryWeightage,
                displayCategoryWeightage: totalScore,
                actualWeightage: _.get(_.first(scoreValues), "value", ""),
              });
            }

            if (ibGradeEnabled) {
              const defaultGradeSet = _.find(filteredCriteriaSets, {
                criteriaType: "FINAL_GRADE",
                subType: "SUBJECT",
              });

              //academicCriteriaSet ->  is the id of the default local grade criteria set
              //categoryId -> is the id of the category
              //ibPYPElementId ->: is the id of the subject
              const {
                rating: matchedRating,
                showWarning,
                calculatedGrade,
              } = getOverallRating({
                overallGradeSet: defaultGradeSet,
                ratings,
                subjectId,
                categoryId: id,
                criteriaSet: { id, label, criteriaType },
                curriculumType,
                subjectType,
                academicCriteriaSets,
              });

              const categoryGradeEquivalent = _.get(
                matchedRating,
                "value",
                "-"
              );

              let updatedOptions = _.map(
                _.get(defaultGradeSet, "academicCriteriaValues", []),
                option => {
                  return {
                    label: option.label,
                    value: option.id,
                  };
                }
              );

              if (
                categoryGradeEquivalent != "-" ||
                isGradingHistoryEnhancementsEnabledInReports
              ) {
                updatedOptions = _.concat(
                  [{ label: "-", value: "overall_grade" }],
                  updatedOptions
                );
              }

              value.push({
                valueId: _.get(
                  _.find(updatedOptions, option => {
                    return option.label == categoryGradeEquivalent;
                  }),
                  "value",
                  "overall_grade"
                ),
                id: "IB_GRADE",
                value: categoryGradeEquivalent,
                label: categoryGradeLabel,
                options: updatedOptions,
                showOverallGradeDropdown: true,
                disabled:
                  finalGradeCalculationSetting != "MANUAL" &&
                  !overrideFinalGradeSetting,
                academicCriteriaSetId: defaultGradeSet?.id,
                academicCriteriaValue: id,
                showWarning:
                  showWarning && finalGradeCalculationSetting != "MYP_CRITERIA",
                calculatedGrade,
              });
            }

            isRatingEnabled = finalScoreEnabled || ibGradeEnabled;
          }

          const showInfoIcon =
            getSettingValue({
              name: "FinalGradeCalculationSetting",
              currentCurriculumProgramId,
            }) != "IB_GRADE";

          result.push({
            label,
            value,
            isRatingEnabled,
            criteriaType: "ASSESSMENT_CATEGORY",
            showInfoIcon,
            id,
            weightage,
            totalScore,
            score,
            showWeightage: useWeightageForCategory,
            gradeScaleType,
          });
        }
      );
    }

    if (criteriaType == "CUSTOM") {
      const { id: gradeId, itemId, pypType } = gradeNode;

      const academicCriteriaValue = _.find(criteriaSet.academicCriteriaValues, {
        id: getStudentElementRatingValueOfCriteriaSet({
          setId: criteriaSet.id,
          type: pypElementMapping[pypType],
          nodeId: gradeId,
          itemId,
          ratings,
          ...(!_.isEmpty(termId) ? { termId, gradingPeriodType } : {}),
        }),
      });

      result.push({
        ...criteriaSet,
        id: criteriaSet.id,
        label: criteriaSet.label,
        value: _.get(academicCriteriaValue, "abbreviation", "-"),
        valueId: _.get(academicCriteriaValue, "id"),
      });
    }

    if (criteriaType == "AGGREGATE") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      // The Aggregate Parent Criteria Set refers to the criteria set being aggregated.
      // For example, in case of "Total for Credit", the "Credit" criteria set acts as the aggregate parent.
      // Similarly, in case of "Average for Overall Score", the "Overall Score" criteria set is the aggregate parent.
      const aggregateParentCriteriaSetType = _.get(
        aggregateParentCriteriaSet,
        "criteriaType"
      );

      const aggregateRatings = _.includes(
        GPA_ASSOCIATED_CRITERIA_TYPES_ELIGIBLE_FOR_AGGREGATION,
        aggregateParentCriteriaSetType
      )
        ? _.get(gpaAssociatedData, "aggregateRatings")
        : ratings;

      const aggregateRatingCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings: aggregateRatings,
        gradingPeriodType,
        termId,
        curriculumType,
        criteriaParentType,
        aggregateParentCriteriaSet,
      });

      result.push(aggregateRatingCriteriaSet);
    }

    if (criteriaType == "PROJECT_FINAL_SCORE") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const finalGradeCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings,
        subjectId,
        gradingPeriodType,
        termId,
        curriculumType,
      });

      result.push(finalGradeCriteriaSet);
    }

    if (criteriaType == "PROJECT_FINAL_GRADE") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const finalGradeCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings,
        subjectId,
        gradingPeriodType,
        termId,
        curriculumType,
      });

      result.push(finalGradeCriteriaSet);
    }

    if (criteriaType == "CRITERIA_RUBRIC_FINAL_GRADE") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const finalGradeCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings,
        subjectId,
        gradingPeriodType,
        termId,
        curriculumType,
      });

      result.push(finalGradeCriteriaSet);
    }

    if (criteriaType == "PROJECT_OVERALL_PROGRESS") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const overallProgressCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings,
        subjectId,
        gradingPeriodType,
        termId,
        curriculumType,
      });

      result.push(overallProgressCriteriaSet);
    }

    if (
      criteriaType == MYP_CRITERIA_FINAL_SCORE &&
      getIsMYPCriteriaTotalAsAggregateEnabled()
    ) {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const mypCriteriaFinalScoreCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings,
        subjectId,
        gradingPeriodType,
        termId,
        curriculumType,
      });

      result.push(mypCriteriaFinalScoreCriteriaSet);
    }

    if (criteriaType == "GRADE_POINT") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const gradePointRatingCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings: _.get(gpaAssociatedData, "gpRatings"),
        subjectId,
        termId,
        gpaExclusionMap: _.get(gpaAssociatedData, "gpaExclusionMap", {}),
        isFreezed,
      });

      result.push(gradePointRatingCriteriaSet);
    }

    if (criteriaType == "CREDIT") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const creditRatingCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings: _.get(gpaAssociatedData, "credits"),
        subjectId,
        termId,
      });

      result.push(creditRatingCriteriaSet);
    }

    if (criteriaType == "QUALITY_POINT") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const qualityPointRatingCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings: _.get(gpaAssociatedData, "qualityPoints"),
        subjectId,
        termId,
      });

      result.push(qualityPointRatingCriteriaSet);
    }

    if (criteriaType == "GRADE_POINT_AVERAGE") {
      const getCriteriaSet =
        CURRICULUM_CRITERIA__MAP[curriculumType][criteriaType];

      const gpaRatingCriteriaSet = getCriteriaSet({
        criteriaSet,
        ratings: _.get(gpaAssociatedData, "gpaRatings"),
        termId,
      });

      result.push(gpaRatingCriteriaSet);
    }
  });

  return !_.isUndefined(isRatingEnabled)
    ? _.filter(
        result,
        item => _.get(item, "isRatingEnabled") == isRatingEnabled
      )
    : result;
};

export const getStudentElementRatingTotal = ({
  itemId = "il",
  ratings,
  subjectObjectivesMap,
  academicCriteriaSets,
  gradingPeriodType,
  termId,
  returnZeroValue = false,
  uid,
  node,
  calculateMYPCriteriaTotalMethod,
  isDPProjectCriteria,
  isMYPProjectCriteria,
}) => {
  const isMYPCriteriaTotalAsAggregateEnabled = getIsMYPCriteriaTotalAsAggregateEnabled();

  if (
    isMYPCriteriaTotalAsAggregateEnabled &&
    !isDPProjectCriteria &&
    !isMYPProjectCriteria
  ) {
    const dynamicCriteriaSets = getDynamicGradeSets({
      academicCriteriaSets,
      dynamicCriteriaTypes: [MYP_CRITERIA_FINAL_SCORE],
      subjectId: itemId == "il" ? null : itemId,
      ratings,
      gradingPeriodType,
      termId,
      curriculumType: CURRICULUM_TYPE_MYP,
    });

    const mypCriteriaFinalScoreCriteriaSet = _.get(
      dynamicCriteriaSets,
      "0",
      {}
    );
    return _.get(mypCriteriaFinalScoreCriteriaSet, "value", "-");
  }
  const ibDefinedOrProjectSet = _.find(academicCriteriaSets, {
    criteriaType: isDPProjectCriteria ? "PROJECT_CRITERIA_GRADE" : "IB_DEFINED",
  });

  const filteredRatings = getMypCriteriaRatings({
    ratings,
    itemId,
    subjectObjectivesMap,
    setId: _.get(ibDefinedOrProjectSet, "id"),
    gradingPeriodType,
    termId,
    uid,
    node,
  });

  const modifiedFilteredRatings = _.uniqBy(filteredRatings, "nodeId");

  if (
    _.size(modifiedFilteredRatings) === 4 ||
    (_.size(modifiedFilteredRatings) === 3 &&
      _.includes(["PP_CRITERIA"], uid)) ||
    isMYPProjectCriteria ||
    isDPProjectCriteria ||
    // IL only has 3 criteria so we will calculate total in case of presence of 3 ratings
    (_.size(modifiedFilteredRatings) === 3 &&
      _.includes(
        [
          "IL_GRADE_DESCRIPTOR",
          "INTERDISCIPLINARY_GRADES",
          "INTERDISCIPLINARY_OBJECTIVES",
        ],
        uid
      ) &&
      getIsEnableFinalGradeForIL()) ||
    //In case when all criteria ratings are not required to calculate a total.
    (calculateMYPCriteriaTotalMethod != MYP_CRITERIA_TOTAL_TYPE_ALL_CRITERIA &&
      !_.isEmpty(modifiedFilteredRatings))
  ) {
    const total = calculateMypCriteriaRatingsTotal({
      mypCriteriaRatings: modifiedFilteredRatings,
      academicCriteriaSetValues: _.get(
        ibDefinedOrProjectSet,
        "academicCriteriaValues",
        []
      ),
      isDPProjectCriteria,
    });
    if (total == 0 && !returnZeroValue) {
      return "-";
    }
    return total;
  }
  return "-";
};

export const getMYPFinalGrade = ({
  itemId,
  subjectObjectivesMap = {},
  ratings,
  academicCriteriaSets,
  uid,
  finalGradeCalculationSetting,
  overrideFinalGradeSetting,
  gradeNode,
  calculateFromRatings = false,
  calculateMYPCriteriaTotalMethod,
  gradingPeriodType,
  termId,
}) => {
  const total = getStudentElementRatingTotal({
    itemId,
    subjectObjectivesMap,
    ratings,
    academicCriteriaSets,
    uid,
    calculateMYPCriteriaTotalMethod,
    gradingPeriodType,
    termId,
  });

  const dynamicCriteriaSets = getDynamicGradeSets({
    total,
    academicCriteriaSets,
    isRatingEnabled: true,
    uid,
    ...(calculateFromRatings
      ? {
          shouldNotCalculateFinalGradeFromTotal: true,
          gradeNode,
          finalGradeCalculationSetting,
          overrideFinalGradeSetting,
          ratings,
          subjectId: itemId,
          termId,
          gradingPeriodType,
        }
      : {}),
    curriculumType: CURRICULUM_TYPE_MYP,
  });

  const finalGradeSet = _.find(dynamicCriteriaSets, set => {
    return _.get(set, "criteriaType", "") == "FINAL_GRADE";
  });

  const { value = null, overRidingValue = null } = finalGradeSet || {};

  return !overRidingValue || overRidingValue === value || !calculateFromRatings
    ? value
    : null;
};

//this function checkes if we can merge attendanceSubjectSpecific node and merges it with attendance node

export const getAggregateRatingParentsForTemplateSide = ({
  academicCriteriaSets,
  criteriaIdEnabledInAggregateSetting,
}) => {
  return _.reduce(
    academicCriteriaSets,
    (acc, criteriaSet) => {
      if (
        _.includes(criteriaIdEnabledInAggregateSetting, criteriaSet.id) &&
        criteriaSet.showInProgressSummary
      ) {
        acc.push({
          criteriaParentType: criteriaSet.criteriaType,
          label: criteriaSet.label,
        });
      }
      return acc;
    },
    []
  );
};

export const getRatingHistoryQueryVariables = ({
  studentId,
  academicCriteriaSetId,
  elementId,
  elementType,
  updatedSubjectIds,
  isMultiTermReport,
  termIdForMultiTermReport,
  curriculumProgramId,
  learningGoalsRatingEndDate,
}) => {
  const isFromGradebookMultiTerm = !_.isEmpty(learningGoalsRatingEndDate);
  const isMultiTermSupported = isMultiTermReport || isFromGradebookMultiTerm;
  const commonFilters = {
    academicCriteriaSetId,
    ibPYPElementId: elementId,
    // If elementId is not present, then we need to pass ibPYPElementTypes
    ...(elementId
      ? { ibPYPElementType: elementType }
      : { ibPYPElementTypes: [elementType] }),
    last: 30,
  };
  const elementRatingHistoryVariables = {
    studentId,
    filtersForRatingHistory: {
      ...commonFilters,
      subjectIds: updatedSubjectIds,
      ...(isMultiTermSupported
        ? { academicTermId: termIdForMultiTermReport }
        : {}),
    },
    ...(isMultiTermSupported ? { curriculumProgramId } : {}),
  };

  return elementRatingHistoryVariables;
};

export const getMypObjectivesPlaceholderGradeScales = ({ t, gradeScales }) => {
  const mypIbDefinedGradeScale = _.find(gradeScales, {
    type: "MYP_IB_DEFINED",
  });

  const incrementGradeScaleNumberBy = mypIbDefinedGradeScale ? 1 : 0;

  return [
    {
      id: "gradeScale1",
      label: mypIbDefinedGradeScale
        ? mypIbDefinedGradeScale?.displayName
        : t("progressReport:grade_scale_number", {
            number: 1,
          }),
      showInProgressSummary: true,
    },
    {
      id: "gradeScale2",
      label: t("progressReport:grade_scale_number", {
        number: 1 + incrementGradeScaleNumberBy,
      }),
      showInProgressSummary: true,
    },
    {
      id: "gradeScale3",
      label: t("progressReport:grade_scale_number", {
        number: 2 + incrementGradeScaleNumberBy,
      }),
      showInProgressSummary: true,
    },
  ];
};

export const getEnabledGradeScalesInTemplateSettings = ({
  templateSettings,
  settingConfigKey,
}) =>
  _.map(
    _.filter(
      _.get(
        _.get(templateSettings, "gradeScaleConfiguration", []),
        settingConfigKey,
        []
      ),
      { isEnabled: true }
    ),
    "id"
  );

export const getPlaceholderGradeScales = ({
  t,
  settingConfigKey,
  templateSettings,
  academicCriteriaSets,
  isNewGradeScaleSettingsEnabled,
}) => {
  if (isNewGradeScaleSettingsEnabled && !_.isEmpty(settingConfigKey)) {
    const enabledGradeScales = getEnabledGradeScalesInTemplateSettings({
      templateSettings,
      settingConfigKey,
    });

    const categoryScorePlaceholderCriteriaSet = _.find(
      academicCriteriaSets,
      criteriaSet =>
        criteriaSet.criteriaType == "CATEGORY_PLACEHOLDER" &&
        criteriaSet.subType == "SCORE"
    );

    const categoryGradePlaceholderCriteriaSet = _.find(
      academicCriteriaSets,
      criteriaSet =>
        criteriaSet.criteriaType == "CATEGORY_PLACEHOLDER" &&
        criteriaSet.subType == "GRADE"
    );

    const isCategoryScoreEnabled = _.includes(
      enabledGradeScales,
      categoryScorePlaceholderCriteriaSet?.id
    );

    const isCategoryGradeEnabled = _.includes(
      enabledGradeScales,
      categoryGradePlaceholderCriteriaSet?.id
    );

    const isCategoryEnabled = isCategoryScoreEnabled || isCategoryGradeEnabled;

    return [
      ...(isCategoryEnabled
        ? [
            {
              id: "category1",
              label: t("common:category"),
              showInProgressSummary: true,
              children: [
                ...(isCategoryScoreEnabled
                  ? [
                      {
                        id: categoryScorePlaceholderCriteriaSet?.id,
                        label: categoryScorePlaceholderCriteriaSet?.label,
                        showInProgressSummary: true,
                      },
                    ]
                  : []),
                ...(isCategoryGradeEnabled
                  ? [
                      {
                        id: categoryGradePlaceholderCriteriaSet?.id,
                        label: categoryGradePlaceholderCriteriaSet?.label,
                        showInProgressSummary: true,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        id: "gradeScale1",
        label: t("progressReport:grade_scale_number", {
          number: 1,
        }),
        showInProgressSummary: true,
      },
      {
        id: "gradeScale2",
        label: t("progressReport:grade_scale_number", {
          number: 2,
        }),
        showInProgressSummary: true,
      },
    ];
  }

  return [
    {
      id: "gradeScale1",
      label: t("progressReport:grade_scale_number", {
        number: 1,
      }),
      showInProgressSummary: true,
    },
    {
      id: "gradeScale2",
      label: t("progressReport:grade_scale_number", {
        number: 2,
      }),
      showInProgressSummary: true,
    },
    {
      id: "gradeScale3",
      label: t("progressReport:grade_scale_number", {
        number: 3,
      }),
      showInProgressSummary: true,
    },
  ];
};

export const getProgressionPointDependentGradeScales = ({ templateBody }) => {
  const { version } = templateBody;

  const gradeScaleOrAcs =
    version == 3.0
      ? _.get(templateBody, "gradeScales", [])
      : _.get(templateBody, "academicCriteriaSets", []);

  const dependentGradeScales = _.get(
    templateBody,
    "progressionPointConfig.gradeScales"
  );
  const dependentGradeScaleIds = _.map(dependentGradeScales, "id");

  const progressionPointDependentGradeScales = _.filter(gradeScaleOrAcs, item =>
    _.includes(dependentGradeScaleIds, _.get(item, "id"))
  );

  return progressionPointDependentGradeScales;
};

export const getMypFinalGradeConfig = ({
  isNewGradingSetup,
  gradeScales,
  academicCriteriaSets,
  academicCriteriaValues,
  criteriaType,
  uid,
  organizationId,
  curriculumProgramId,
}) => {
  const isNewSetup = isNewGradingSetup && !_.isEmpty(gradeScales);

  if (isNewSetup) {
    const finalGradeScale = _.find(gradeScales, { type: MYP_FINAL_GRADE });

    return finalGradeScale
      ? {
          updatedCriteriaValues: _.orderBy(
            transformGradeScaleValuesToCriteriaValues(
              _.get(
                finalGradeScale,
                "gradeScaleEntityDetails[0].gradeScaleValues",
                []
              )
            ),
            ["displaySequence"],
            ["desc"]
          ),
          criteriaLabels: {
            [FINAL_GRADE]: _.get(finalGradeScale, "displayName", ""),
          },
          criteriaSet: null,
        }
      : { updatedCriteriaValues: [], criteriaLabels: {}, criteriaSet: null };
  }

  const criteriaSet = _.find(academicCriteriaSets, { criteriaType });

  const criteriaSetValues = _.isEmpty(academicCriteriaValues)
    ? _.sortBy(_.get(criteriaSet, "academicCriteriaValues", []), "abbreviation")
    : academicCriteriaValues;

  return {
    criteriaSet,
    updatedCriteriaValues: getUpdatedCriteriaValuesForMypFinalGrade({
      academicCriteriaSets,
      academicCriteriaValues: criteriaSetValues,
      uid,
    }),
    criteriaLabels: getCriteriaSetLabels({
      organizationId,
      curriculumProgramId,
      criteriaTypes: [FINAL_GRADE],
      academicCriteriaSets,
    }),
  };
};

export const sortEntityGradeScalesByTemplateOrder = ({ entityGradeScales }) => {
  return _.sortBy(entityGradeScales, scale =>
    _.get(scale, "d_order", -Infinity)
  );
};

export const getGradeSummaryConfigNode = nodeUID => {
  switch (nodeUID) {
    case "PROGRESS_SUMMARY_COURSES":
    case "PROGRESS_SUMMARY_SUBJECTS":
    case "SUBJECT":
    case "COURSE":
      return "PROGRESS_SUMMARY_GRADES";
    case "PROGRESS_SUMMARY_MYP_PROJECTS":
    case "PROGRESS_SUMMARY_MYP_PROJECTS_ELEMENT":
      return "PROGRESS_SUMMARY_MYP_GRADES";
    case "PROGRESS_SUMMARY_DP_CORE":
    case "PROGRESS_SUMMARY_DP_CORE_ELEMENT":
      return "PROGRESS_SUMMARY_DP_CORE_GRADES";
    case "PROGRESS_SUMMARY_CP_CORE":
    case "PROGRESS_SUMMARY_CP_CORE_ELEMENT":
      return "PROGRESS_SUMMARY_CP_CORE_GRADES";
    default:
      return null;
  }
};

export const getStudentElementRatingOfGradeScale = ({
  ratings,
  type,
  itemId,
  setId,
  isComputedRating = false,
  termId,
  checkCategoryId = false,
  categoryId,
  shouldCheckComputedRating = true,
  globalCategoryId = null,
}) => {
  return _.find(ratings, ratingObj => {
    let isMatched = ratingObj.entityType == type;
    const computedRatingCheck =
      _.has(ratingObj, "isComputedRating") && shouldCheckComputedRating
        ? ratingObj.isComputedRating == isComputedRating
        : true;

    if (itemId) {
      isMatched = ratingObj.entityId == itemId && isMatched;
    }

    const isTermIdMatched = ratingObj.academicTermId === termId;

    let isCategoryIdMatched = true;
    if (checkCategoryId && _.has(ratingObj, "categoryId")) {
      isCategoryIdMatched = ratingObj.categoryId === categoryId;
    }

    let globalCategoryIdMatch = true;
    if (!_.isEmpty(globalCategoryId) && _.has(ratingObj, "globalCategoryId")) {
      globalCategoryIdMatch = ratingObj.globalCategoryId === globalCategoryId;
    }

    return (
      isMatched &&
      ratingObj.academicCriteriaSet == setId &&
      computedRatingCheck &&
      isTermIdMatched &&
      isCategoryIdMatched &&
      globalCategoryIdMatch
    );
  });
};

export const getMypCriteriaRatings = ({
  ratings,
  setId,
  subjectObjectivesMap,
  itemId,
  gradingPeriodType,
  termId,
  uid,
  node,
}) => {
  let resolvedItemId = itemId;
  if (!resolvedItemId) {
    resolvedItemId = "il";
  }
  const subjectObjectives = _.get(subjectObjectivesMap, [resolvedItemId], {});
  return _.filter(ratings, ratingObj => {
    let isMatched = true;
    if (gradingPeriodType == "CUMULATIVE") {
      isMatched = ratingObj.academicTermId == termId;
    }
    if (_.includes(["PP_CRITERIA", "CP_CRITERIA"], uid)) {
      return (
        ratingObj.ibPYPElementType == "MYP_OBJECTIVES" &&
        isMatched &&
        ratingObj.academicCriteriaSet == setId &&
        _.includes(node.children, ratingObj.nodeId)
      );
    }
    if (_.includes(["EE_ASSESSMENT_CRITERIA", "RP_ASSESSMENT_CRITERIA"], uid)) {
      return (
        ratingObj.ibPYPElementType == "DP_CRITERIA" &&
        isMatched &&
        ratingObj.academicCriteriaSet == setId &&
        _.includes(node.children, ratingObj.nodeId)
      );
    }
    return (
      ratingObj.ibPYPElementType == "MYP_OBJECTIVES" &&
      isMatched &&
      ratingObj.academicCriteriaSet == setId &&
      _.includes(subjectObjectives, ratingObj.nodeId)
    );
  });
};

export const calculateMypCriteriaRatingsTotal = ({
  mypCriteriaRatings,
  academicCriteriaSetValues,
  isDPProjectCriteria,
}) => {
  return _.reduce(
    mypCriteriaRatings,
    (result, rating) => {
      if (isDPProjectCriteria) {
        const ratingValue = _.get(rating, "value");
        return result + parseInt(ratingValue || 0);
      } else {
        const academicCriteriaValue = _.get(rating, "academicCriteriaValue");
        const criteriaSetValue = _.find(academicCriteriaSetValues, {
          id: academicCriteriaValue,
        });
        return result + parseInt(_.get(criteriaSetValue, "abbreviation", 0));
      }
    },
    0
  );
};

export const getStudentElementRatingValueOfGradeScale = params => {
  const { isNumericValueCalculationMethod = false, type } = params || {};
  const rating = getStudentElementRatingOfGradeScale(params);
  if (
    isNumericValueCalculationMethod &&
    _.includes(
      [
        ELEMENT_TYPE_UBD_LEARNING_STANDARD,
        ELEMENT_TYPE_DP_LEARNING_STANDARD,
        ELEMENT_TYPE_MYP_LEARNING_STANDARD,
        ELEMENT_TYPE_PYP_BENCHMARK,
      ],
      type
    )
  ) {
    return _.get(rating, "value", _.get(rating, "academicCriteriaValue", null));
  }
  return _.get(rating, "academicCriteriaValue", null);
};

export const updateScoreAccordingToDecimalSetting = ({
  score,
  decimalTypeForCategoryAndFinalScore,
  decimalPointsForCategoryAndFinalScore,
}) => {
  const roundedValue = _.round(score, 5);

  switch (decimalTypeForCategoryAndFinalScore) {
    // Truncate and format the number according to decimal setting
    case "TRUNCATE": {
      // split the number into integer and decimal parts
      const [integer, decimals] = _.split(_.toString(roundedValue), ".");

      // truncate the decimal part to the required decimal points
      const truncatedNumber = parseFloat(
        `${integer}.${decimals?.substring(
          0,
          decimalPointsForCategoryAndFinalScore
        )}`
      );

      return truncatedNumber.toFixed(decimalPointsForCategoryAndFinalScore);
    }

    // Round and format the number according to decimal setting
    case "ROUND":
    default: {
      const roundedNumber = _.round(
        roundedValue,
        decimalPointsForCategoryAndFinalScore
      );
      return roundedNumber.toFixed(decimalPointsForCategoryAndFinalScore);
    }
  }
};

export const getGradingPeriodWeightage = ({
  associatedAcademicTermEntities,
  gradingPeriodId,
}) => {
  const associatedAcademicTermEntity = _.find(associatedAcademicTermEntities, {
    academicTerm: {
      id: gradingPeriodId,
    },
  });

  const weightage = _.get(
    associatedAcademicTermEntity,
    "cumulativeAcademicTermWeightages.0.percentage",
    0
  );

  return weightage;
};
